import React, { useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { SocialShareWidget } from "@/components/SocialShareWidget";
import SEO from "@/components/seo/SEO";
import { ArticleCard } from "@/components/content/ArticleCard";
import { nieuwsArticles, nieuwsCategories } from "@/data/sample-content";
import { Badge } from "@/components/ui/badge";
import { NewsletterPopup, useNewsletterPopup } from "@/components/NewsletterPopup";

const NieuwsIndex = () => {
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [showImages, setShowImages] = useState(false);
  const { showPopup, closePopup } = useNewsletterPopup();

  const filtered = nieuwsArticles.filter((p) => {
    const matchesQuery = !query || (
      p.title.toLowerCase().includes(query.toLowerCase()) ||
      p.excerpt.toLowerCase().includes(query.toLowerCase()) ||
      p.keywords.join(" ").toLowerCase().includes(query.toLowerCase())
    );
    const matchesCategory = !activeCategory || p.category === activeCategory;
    return matchesQuery && matchesCategory;
  });

  const featuredPost = nieuwsArticles.find(p => p.featured);
  const recentPosts = filtered.filter(p => !p.featured);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    url: typeof window !== "undefined" ? window.location.href : "",
    name: "SOL-VI Nieuws",
    description: "Laatste nieuws van SOL-VI: aanbiedingen, partnerships, projecten en bedrijfsupdates.",
    mainEntity: {
      "@type": "Organization",
      name: "SOL-VI",
      url: "https://sol-vi.nl",
    },
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="SOL-VI Nieuws | Aanbiedingen, Partnerships & Updates"
        description="Blijf op de hoogte van het laatste SOL-VI nieuws: speciale aanbiedingen, nieuwe partnerships, project showcases en bedrijfsupdates."
        type="website"
        jsonLd={jsonLd}
      />
      <Header />
      
      <main className="py-32">
        <div className="container mx-auto flex flex-col items-center gap-16 lg:px-16">
          <div className="text-center">
            <Badge variant="secondary" className="mb-6">
              Laatste Updates
            </Badge>
            <h2 className="mb-3 text-pretty text-3xl font-semibold md:mb-4 md:text-4xl lg:mb-6 lg:max-w-3xl lg:text-5xl">
              SOL-VI Nieuws
            </h2>
            <p className="mb-8 text-muted-foreground md:text-base lg:max-w-2xl lg:text-lg">
              Blijf op de hoogte van onze laatste aanbiedingen, partnerships, succesvolle projecten 
              en belangrijke bedrijfsupdates.
            </p>
            <div className="flex items-center justify-center gap-4 mb-8">
              <span className="text-sm text-muted-foreground">Afbeeldingen</span>
              <button
                onClick={() => setShowImages((v) => !v)}
                className={`h-8 w-14 rounded-full border transition-colors ${
                  showImages ? "bg-primary text-primary-foreground" : "bg-muted"
                }`}
                aria-pressed={showImages}
                aria-label="Schakel afbeeldingen in/uit"
              >
                <span className={`block h-7 w-7 bg-background rounded-full shadow transform transition-transform m-0.5 ${showImages ? "translate-x-6" : "translate-x-0"}`} />
              </button>
            </div>
          </div>

        {/* Featured Post */}
        {featuredPost && (
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px bg-gradient-to-r from-primary/50 to-transparent flex-1"></div>
              <h2 className="text-2xl font-bold text-primary">Uitgelicht</h2>
              <div className="h-px bg-gradient-to-l from-primary/50 to-transparent flex-1"></div>
            </div>
            <article className="group relative overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 via-background to-accent/5 hover:shadow-2xl transition-all duration-500">
              {showImages && featuredPost.imageUrl ? (
                <div className="relative h-64 md:h-80 overflow-hidden">
                  <img 
                    src={featuredPost.imageUrl}
                    alt={featuredPost.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                </div>
              ) : (
                <div className="relative h-64 md:h-80 flex items-center justify-center bg-gradient-to-br from-primary/20 to-accent/20">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="text-2xl font-bold text-primary">
                        {featuredPost.category.charAt(0)}
                      </span>
                    </div>
                    <Badge className="bg-primary text-primary-foreground">
                      {featuredPost.category}
                    </Badge>
                  </div>
                </div>
              )}
              
              <div className="p-8">
                <h3 className="text-3xl md:text-4xl font-bold mb-4 leading-tight group-hover:text-primary transition-colors">
                  {featuredPost.title}
                </h3>
                <p className="text-muted-foreground text-lg leading-relaxed mb-6">{featuredPost.excerpt}</p>
                
                <div className="flex flex-wrap gap-2 mb-6">
                  {featuredPost.tags.slice(0, 4).map((tag) => (
                    <span key={tag} className="text-sm px-3 py-1.5 rounded-full bg-accent/30 text-foreground font-medium hover:bg-accent/50 transition-colors">
                      {tag}
                    </span>
                  ))}
                </div>
                
                <Button variant="cta" size="lg" onClick={() => window.location.href = `/nieuws/${featuredPost.slug}`}>
                  Lees volledig artikel
                  <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              </div>
            </article>
          </section>
        )}

          {/* Filter Section */}
          <div className="mb-8 flex flex-col items-center">
            <div className="flex flex-col md:flex-row gap-4 mb-6 w-full max-w-2xl">
              <input
                aria-label="Zoek in nieuws"
                placeholder="Zoek in nieuws..."
                className="flex-1 rounded-lg border border-border bg-background px-4 py-3 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            
            <div className="flex flex-wrap gap-2 justify-center">
              <Button 
                variant={activeCategory === null ? "default" : "outline"} 
                onClick={() => setActiveCategory(null)}
                className="rounded-full"
              >
                Alle categorieën
              </Button>
              {nieuwsCategories.map((category) => (
                <Button 
                  key={category.key} 
                  variant={activeCategory === category.key ? "default" : "outline"} 
                  onClick={() => setActiveCategory(category.key)}
                  className="rounded-full"
                >
                  {category.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Recent Posts Grid */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {recentPosts.map((article) => (
              <ArticleCard
                key={article.id}
                article={article}
                showImages={showImages}
                linkTo={`/nieuws/${article.slug}`}
              />
            ))}
          </div>
        </div>

        {/* Social Follow Section */}
        <section className="mt-12 bg-gradient-to-r from-secondary/5 to-primary/5 rounded-lg p-6">
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold mb-2">Blijf op de hoogte!</h2>
            <p className="text-muted-foreground">Volg ons voor de laatste updates en tips</p>
          </div>
          <div className="flex justify-center">
            <SocialShareWidget 
              variant="follow"
              size="md"
            />
          </div>
        </section>

        {/* CTA Section */}
        <section className="mt-16 text-center bg-gradient-to-r from-primary/5 to-accent/10 rounded-lg p-8">
          <h2 className="text-2xl font-bold mb-4">Mis geen enkel nieuws meer!</h2>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Meld u aan voor onze nieuwsbrief en ontvang als eerste informatie over nieuwe aanbiedingen, 
            partnerships en projecten.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="cta" 
              size="lg"
              onClick={() => (window as any).Calendly?.initPopupWidget?.({ url: "https://calendly.com/sol-vi/new-meeting?primary_color=ff7800" })}
            >
              Plan adviesgesprek
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => (window as any).renderOpenSolarWidget?.()}
            >
              Vraag offerte aan
            </Button>
          </div>
        </section>
      </main>
      
      <Footer />
      <NewsletterPopup isOpen={showPopup} onClose={closePopup} />
    </div>
  );
};

export default NieuwsIndex;